/**
 * How many tattoo leads the case has open (ADR-0061): the pairs of similar tattoos on two
 * different persons that wait for a decision or for a merge. The server answers two numbers and
 * names no person, mark or pair, so the read is not audited and is polled like the face count,
 * for the Entity Resolution tab and the case rail. Tattoo matching is HUMINT's: with the
 * extension off the count is never read. The rail loads this with the desktop app, so it imports
 * nothing of the HUMINT workspace.
 */
import { useQuery } from '@tanstack/react-query'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { ApiError } from '@/services/api/client'
import { humintTattoosApi, type TattooLeadCountDto } from '@/services/api/humint-tattoos'

/** Under the case's tattoo reads (`tattooKeys.all`): a merge made there leaves none stale. */
export const tattooLeadKeys = {
  all: (caseId: string) => ['case', caseId, 'humint', 'tattoos', 'leads'] as const,
  list: (caseId: string) => [...tattooLeadKeys.all(caseId), 'list'] as const,
  count: (caseId: string) => [...tattooLeadKeys.all(caseId), 'count'] as const,
}

export interface TattooLeadCount {
  toReview: number
  toMerge: number
  /** Every open lead: the number a badge shows. */
  total: number
}

const POLL_MS = 60 * 1000

/** The count as the badges read it; nothing is counted before the first answer. */
export function toLeadCount(dto: TattooLeadCountDto | undefined): TattooLeadCount {
  const toReview = dto?.to_review ?? 0
  const toMerge = dto?.to_merge ?? 0
  return { toReview, toMerge, total: toReview + toMerge }
}

/**
 * When to ask again after `error` (null: the last read answered). A refusal the same request
 * would get again (not a member of the case) ends the poll; an outage does not.
 */
export function leadCountPoll(error: unknown): number | false {
  const refused = error instanceof ApiError && error.status >= 400 && error.status < 500
  return refused ? false : POLL_MS
}

/** The count's query: its key, its read and its poll; `humint` false and it is never read. */
export function leadCountQuery(caseId: string, humint: boolean) {
  return {
    queryKey: tattooLeadKeys.count(caseId),
    queryFn: ({ signal }: { signal: AbortSignal }) => humintTattoosApi.leadsCount(caseId, signal),
    enabled: humint && Boolean(caseId),
    staleTime: POLL_MS,
    // Matching runs in the background; the next poll is the retry of a failed one.
    refetchInterval: (query: { state: { error: unknown } }) => leadCountPoll(query.state.error),
    retry: false,
  }
}

/** The case's open tattoo leads, shared by the rail and the ER tab; null while HUMINT is off. */
export function useTattooLeadCount(caseId: string): TattooLeadCount | null {
  const { enabled } = useExtensionEnabled(HUMINT_EXTENSION_ID)
  const { data } = useQuery(leadCountQuery(caseId, enabled))
  return enabled ? toLeadCount(data) : null
}
